/**
 * Textarea with all the trimmings on video and article pages beneath the content
 * that allow users to submit different discussion types.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {border, color} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslate} from "@khan/i18n";

import * as sharedDiscussionStyles from "../shared-styles.ts";

const DiscussionFormattingOptions = (): React.ReactElement<
    React.ComponentProps<"table">
> => (
    <table className={css(styles.formattingOptionsTable)}>
        <thead>
            <tr
                className={css(
                    styles.formattingOptionsRow,
                    styles.formattingOptionsHeaderRow,
                )}
            >
                <th scope="col">
                    <View style={styles.formattingOptionsRowContent}>
                        {t`Typing this`}
                    </View>
                </th>

                <th scope="col">
                    <View style={styles.formattingOptionsRowContent}>
                        {t`Displays this`}
                    </View>
                </th>
            </tr>
        </thead>
        <tbody>
            <tr className={css(styles.formattingOptionsRow)}>
                <td>
                    <View style={styles.formattingOptionsRowContent}>
                        *{t`bold`}*
                    </View>
                </td>
                <td>
                    <View
                        style={styles.formattingOptionsRowContent}
                        aria-labelledby="bold"
                        aria-describedby="boldDescription"
                    >
                        <LabelLarge id="bold">{t`bold`}</LabelLarge>
                        <span className="sr-only" id="boldDescription">
                            {t`Emphasize text using a bolded typeface.`}
                        </span>
                    </View>
                </td>
            </tr>
            <tr className={css(styles.formattingOptionsRow)}>
                <td>
                    <View style={styles.formattingOptionsRowContent}>
                        {doNotTranslate`_`}
                        {t`italics`}
                        {doNotTranslate`_`}
                    </View>
                </td>
                <td>
                    <View
                        style={[
                            styles.formattingOptionsRowContent,
                            styles.italic,
                        ]}
                        aria-labelledby="italics"
                        aria-describedby="italicsDescription"
                    >
                        <span id="italics">{t`italics`}</span>
                        <span className="sr-only" id="italicsDescription">
                            {t`Emphasize text using an italicized typeface.`}
                        </span>
                    </View>
                </td>
            </tr>
            <tr className={css(styles.formattingOptionsRow)}>
                <td>
                    <View style={styles.formattingOptionsRowContent}>
                        `{t`code`}`
                    </View>
                </td>
                <td>
                    <View
                        style={[
                            styles.formattingOptionsRowContent,
                            styles.code,
                        ]}
                        aria-labelledby="code"
                        aria-describedby="codeDescription"
                    >
                        <span id="code">{t`code`}</span>
                        <span className="sr-only" id="codeDescription">
                            {t`Emphasize text using a monospaced typeface.`}
                        </span>
                    </View>
                </td>
            </tr>

            <tr className={css(styles.formattingOptionsRow)}>
                <td>
                    <View style={styles.formattingOptionsRowContent}>
                        <View>{doNotTranslate`\`\`\``}</View>
                        <View>{doNotTranslate`if (x < 0) {`}</View>
                        <View>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            {doNotTranslate`return;`}
                        </View>
                        <View>{doNotTranslate`}`}</View>
                        <View>{doNotTranslate`\`\`\``}</View>
                    </View>
                </td>
                <td>
                    <View
                        style={[
                            styles.formattingOptionsRowContent,
                            styles.code,
                        ]}
                        aria-describedby="codeSnippetDescription"
                    >
                        <View>{doNotTranslate`if (x < 0) {`}</View>
                        <View>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            {doNotTranslate`return;`}
                        </View>
                        <View>{doNotTranslate`}`}</View>

                        <span className="sr-only" id="codeSnippetDescription">
                            {t`Emphasize text using a monospaced typeface.`}
                        </span>
                    </View>
                </td>
            </tr>
        </tbody>
    </table>
);

const styles = StyleSheet.create({
    formattingOptionsTable: {
        width: "100%",
        marginTop: 14,
    },

    formattingOptionsRow: {
        borderTop: `${border.width.thin} solid ${color.fadedOffBlack16}`,
        borderBottom: `${border.width.thin} solid ${color.fadedOffBlack16}`,
    },

    formattingOptionsHeaderRow: {
        backgroundColor: sharedDiscussionStyles.secondaryBackgroundColor,
        textAlign: "left",
        textTransform: "uppercase",
    },

    formattingOptionsRowContent: {
        padding: "14px 16px 16px 16px",
    },

    italic: {
        fontStyle: "italic",
    },

    code: {
        fontFamily: "Inconsolata",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    codeSnippet: {
        height: 231,
    },
});

export default DiscussionFormattingOptions;
